import type { CanvasserStatsItem, VendorSummaryItem, CampaignBenchmarks, ContactAnalyticsResponse, VendorPerformanceResponse } from '@signalvote/shared';
import { AlertTriangle } from 'lucide-react';
import { Card, CardContent } from '../ui/card';
import { Skeleton } from '../ui/skeleton';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';
import { CanvasserTable } from './CanvasserTable';
import { getReliabilityColor, getSurveyColor } from './colorUtils';
import { TrendIcon } from './TrendIcon';
import { VendorShiftSummary } from './VendorShiftSummary';
import { VendorContactOutcomes } from './VendorContactOutcomes';
import { VendorPerformanceTrendChart } from './VendorPerformanceTrendChart';

// ============================================================================
// Types
// ============================================================================

interface SpreadValues {
  min: number;
  max: number;
  median: number;
}

function computeSpread(values: number[]): SpreadValues | null {
  if (values.length === 0) return null;
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  const median =
    sorted.length % 2 === 0 ? (sorted[mid - 1] + sorted[mid]) / 2 : sorted[mid];
  return { min: sorted[0], max: sorted[sorted.length - 1], median };
}

function hasHighVariance(spread: SpreadValues): boolean {
  return spread.min > 0 && spread.max / spread.min > 2;
}

interface VendorDetailProps {
  vendorId: string;
  vendorSummary: VendorSummaryItem[];
  canvasserStats: CanvasserStatsItem[];
  isLoadingSummary: boolean;
  isLoadingStats: boolean;
  onBack: () => void;
  onCanvasserClick: (userId: string) => void;
  benchmarks?: CampaignBenchmarks;
  vendorContactOutcomes?: ContactAnalyticsResponse['contactsByStatus'];
  campaignContactOutcomes?: ContactAnalyticsResponse['contactsByStatus'];
  isLoadingContactOutcomes?: boolean;
  vendorPerformance?: VendorPerformanceResponse;
  isLoadingVendorPerformance?: boolean;
}

// ============================================================================
// Component
// ============================================================================

export function VendorDetail({
  vendorId,
  vendorSummary,
  canvasserStats,
  isLoadingSummary,
  isLoadingStats,
  onBack,
  onCanvasserClick,
  benchmarks,
  vendorContactOutcomes,
  campaignContactOutcomes,
  isLoadingContactOutcomes,
  vendorPerformance,
  isLoadingVendorPerformance,
}: VendorDetailProps) {
  const vendor = vendorSummary.find((v) => v.vendorId === vendorId);

  // Compute canvasser spread metrics
  const doorsPerHourValues = canvasserStats
    .filter((c) => c.hoursWorked > 0)
    .map((c) => c.doorsKnocked / c.hoursWorked);
  const reachRateValues = canvasserStats
    .filter((c) => c.reachRate !== null)
    .map((c) => c.reachRate!);
  const surveyValues = canvasserStats
    .filter((c) => c.surveyCompletionRate !== null)
    .map((c) => c.surveyCompletionRate!);

  const doorsSpread = computeSpread(doorsPerHourValues);
  const reachSpread = computeSpread(reachRateValues);
  const surveySpread = computeSpread(surveyValues);

  const showSpread = canvasserStats.length > 0;
  const anyHighVariance =
    (doorsSpread && hasHighVariance(doorsSpread)) ||
    (reachSpread && hasHighVariance(reachSpread)) ||
    (surveySpread && hasHighVariance(surveySpread));

  // Loading state
  if (isLoadingSummary) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-6 w-48" />
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="h-24" />
          ))}
        </div>
        <Skeleton className="h-64" />
      </div>
    );
  }

  // Not found
  if (!vendor) {
    return (
      <div className="space-y-4">
        <button
          onClick={onBack}
          className="text-sm text-muted-foreground hover:text-foreground"
        >
          Analytics
        </button>
        <p className="py-8 text-center text-muted-foreground">Vendor not found</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Breadcrumb */}
      <nav className="flex items-center gap-2 text-sm text-muted-foreground">
        <button onClick={onBack} className="hover:text-foreground">
          Analytics
        </button>
        <span>/</span>
        <span className="text-foreground">{vendor.vendorName}</span>
      </nav>

      {/* Header */}
      <h2 className="text-xl font-semibold">{vendor.vendorName}</h2>

      {/* Vendor Totals Cards */}
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6">
        <Card>
          <CardContent className="p-4">
            <p className="text-sm text-muted-foreground">Canvassers</p>
            <p className="text-2xl font-bold tabular-nums">{vendor.canvasserCount}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-sm text-muted-foreground">Total Hours</p>
            <p className="text-2xl font-bold tabular-nums">{vendor.totalHours.toFixed(1)}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-sm text-muted-foreground">Doors Knocked</p>
            <div className="flex items-center gap-1">
              <p className="text-2xl font-bold tabular-nums">{vendor.totalDoorsKnocked}</p>
              <TrendIcon direction={vendor.trendDoorsPerHour} />
            </div>
            {benchmarks?.avgDoorsPerHour != null && vendor.totalHours > 0 && (
              <p className="text-xs text-muted-foreground">
                {(vendor.totalDoorsKnocked / vendor.totalHours).toFixed(1)}/hr vs {benchmarks.avgDoorsPerHour.toFixed(1)} avg
              </p>
            )}
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-sm text-muted-foreground">Contacts Made</p>
            <p className="text-2xl font-bold tabular-nums">{vendor.totalContactsMade}</p>
            {benchmarks?.avgContactsPerShift != null && (
              <p className="text-xs text-muted-foreground">
                vs {benchmarks.avgContactsPerShift.toFixed(1)}/shift avg
              </p>
            )}
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-sm text-muted-foreground">Reliability</p>
            <p className={`text-2xl font-bold tabular-nums ${getReliabilityColor(vendor.reliabilityRate)}`}>
              {vendor.reliabilityRate !== null
                ? `${vendor.reliabilityRate.toFixed(1)}%`
                : 'N/A'}
            </p>
            {benchmarks?.avgReliability != null && vendor.reliabilityRate !== null && (
              <p className="text-xs text-muted-foreground">
                vs {benchmarks.avgReliability.toFixed(1)}% avg
              </p>
            )}
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-sm text-muted-foreground">Survey %</p>
            <p className={`text-2xl font-bold tabular-nums ${getSurveyColor(vendor.surveyCompletionRate)}`}>
              {vendor.surveyCompletionRate !== null
                ? `${vendor.surveyCompletionRate.toFixed(1)}%`
                : 'N/A'}
            </p>
            {benchmarks?.avgSurveyCompletion != null && vendor.surveyCompletionRate !== null && (
              <p className="text-xs text-muted-foreground">
                vs {benchmarks.avgSurveyCompletion.toFixed(1)}% avg
              </p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Shift Summary (VDA-001) */}
      <VendorShiftSummary canvasserStats={canvasserStats} isLoading={isLoadingStats} />

      {/* Performance Trend (VDA-002) */}
      <VendorPerformanceTrendChart
        data={vendorPerformance}
        isLoading={isLoadingVendorPerformance ?? false}
      />

      {/* Contact Outcomes (VDA-003) */}
      <VendorContactOutcomes
        vendorOutcomes={vendorContactOutcomes ?? []}
        campaignOutcomes={campaignContactOutcomes ?? []}
        isLoading={isLoadingContactOutcomes}
      />

      {/* Canvasser Spread (ANA-019) */}
      {showSpread && (
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <h3 className="text-sm font-medium">Canvasser Spread</h3>
            {anyHighVariance && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <AlertTriangle
                      className="h-3.5 w-3.5 text-yellow-500"
                      aria-label="High variance"
                    />
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>High variance among canvassers — may indicate individual performance issues rather than vendor-wide training.</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          </div>
          <div className="grid grid-cols-1 gap-2 text-sm sm:grid-cols-3">
            {doorsSpread && (
              <div>
                <span className="text-muted-foreground">Doors/hr: </span>
                <span className="tabular-nums">
                  {doorsSpread.min.toFixed(1)} — {doorsSpread.max.toFixed(1)}, median{' '}
                  {doorsSpread.median.toFixed(1)}
                </span>
              </div>
            )}
            {reachSpread && (
              <div>
                <span className="text-muted-foreground">Reach %: </span>
                <span className="tabular-nums">
                  {reachSpread.min.toFixed(1)} — {reachSpread.max.toFixed(1)}, median{' '}
                  {reachSpread.median.toFixed(1)}
                </span>
              </div>
            )}
            {surveySpread && (
              <div>
                <span className="text-muted-foreground">Survey %: </span>
                <span className="tabular-nums">
                  {surveySpread.min.toFixed(1)} — {surveySpread.max.toFixed(1)}, median{' '}
                  {surveySpread.median.toFixed(1)}
                </span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Canvasser Table (reused from CVA-011) */}
      <CanvasserTable
        canvasserStats={canvasserStats}
        onCanvasserClick={onCanvasserClick}
        isLoading={isLoadingStats}
        benchmarks={benchmarks}
      />
    </div>
  );
}
